import {
  FAIXAS,
  calcularIdade,
  estadoDe,
  foraDoAcompanhamento,
  faixaDaIdade,
  hojeSaoPaulo,
  normalizarNome,
  somarDias,
  soDigitos,
} from "./regras";
import type { EstadoAcompanhamento, FaixaChave } from "./regras";

/** Visitantes por página (padrão do sistema: 10). */
export const VISITANTES_POR_PAGINA = 10;

export type FiltroSituacao = "todos" | EstadoAcompanhamento;
export type FiltroPeriodo = "todos" | "hoje" | "7" | "30" | "90" | "custom";
export type FiltroFaixa = "todas" | FaixaChave | "custom";
export type Ordenacao = "recente" | "nome" | "visitas";

export interface FiltrosVisitantes {
  busca: string;
  situacao: FiltroSituacao;
  periodo: FiltroPeriodo;
  de: string;
  ate: string;
  faixa: FiltroFaixa;
  idadeMin: string;
  idadeMax: string;
  ordem: Ordenacao;
  pagina: number;
}

export const FILTROS_PADRAO: FiltrosVisitantes = {
  busca: "",
  situacao: "todos",
  periodo: "todos",
  de: "",
  ate: "",
  faixa: "todas",
  idadeMin: "",
  idadeMax: "",
  ordem: "recente",
  pagina: 1,
};

const SITUACOES: FiltroSituacao[] = ["todos", "nao_contatado", "contatado", "precisa"];
const PERIODOS: FiltroPeriodo[] = ["todos", "hoje", "7", "30", "90", "custom"];
const ORDENS: Ordenacao[] = ["recente", "nome", "visitas"];
const FAIXAS_VALIDAS: string[] = ["todas", "custom", ...FAIXAS.map((f) => f.chave)];
const DATA_ISO = /^\d{4}-\d{2}-\d{2}$/;

function numeroOuVazio(v: string | null): string {
  return v !== null && /^\d{1,3}$/.test(v) ? v : "";
}

/** Lê os filtros da URL, descartando qualquer valor fora do esperado. */
export function filtrosDaUrl(params: URLSearchParams): FiltrosVisitantes {
  const pick = <T extends string>(
    valor: string | null,
    validos: readonly string[],
    padrao: T,
  ): T => (valor !== null && validos.includes(valor) ? (valor as T) : padrao);
  const pagina = Number.parseInt(params.get("pagina") ?? "1", 10);
  const de = params.get("de") ?? "";
  const ate = params.get("ate") ?? "";
  return {
    busca: (params.get("q") ?? "").slice(0, 100),
    situacao: pick(params.get("situacao"), SITUACOES, "todos"),
    periodo: pick(params.get("periodo"), PERIODOS, "todos"),
    de: DATA_ISO.test(de) ? de : "",
    ate: DATA_ISO.test(ate) ? ate : "",
    faixa: pick(params.get("faixa"), FAIXAS_VALIDAS, "todas"),
    idadeMin: numeroOuVazio(params.get("imin")),
    idadeMax: numeroOuVazio(params.get("imax")),
    ordem: pick(params.get("ordem"), ORDENS, "recente"),
    pagina: Number.isFinite(pagina) && pagina > 0 ? pagina : 1,
  };
}

/** Grava na URL só o que difere do padrão (URL curta e compartilhável). Preserva `aba`. */
export function filtrosParaUrl(f: FiltrosVisitantes, atual: URLSearchParams): URLSearchParams {
  const p = new URLSearchParams();
  const aba = atual.get("aba");
  if (aba) p.set("aba", aba);
  if (f.busca) p.set("q", f.busca);
  if (f.situacao !== "todos") p.set("situacao", f.situacao);
  if (f.periodo !== "todos") p.set("periodo", f.periodo);
  if (f.periodo === "custom") {
    if (f.de) p.set("de", f.de);
    if (f.ate) p.set("ate", f.ate);
  }
  if (f.faixa !== "todas") p.set("faixa", f.faixa);
  if (f.faixa === "custom") {
    if (f.idadeMin) p.set("imin", f.idadeMin);
    if (f.idadeMax) p.set("imax", f.idadeMax);
  }
  if (f.ordem !== "recente") p.set("ordem", f.ordem);
  if (f.pagina > 1) p.set("pagina", String(f.pagina));
  return p;
}

export function temFiltroAtivo(f: FiltrosVisitantes): boolean {
  return f.busca !== "" || f.situacao !== "todos" || f.periodo !== "todos" || f.faixa !== "todas";
}

export function erroPeriodo(f: FiltrosVisitantes): string {
  return f.periodo === "custom" && f.de && f.ate && f.de > f.ate
    ? "A data final deve ser igual ou depois da inicial."
    : "";
}

export function erroIdade(f: FiltrosVisitantes): string {
  if (f.faixa !== "custom" || f.idadeMin === "" || f.idadeMax === "") return "";
  return Number(f.idadeMin) > Number(f.idadeMax)
    ? "A idade máxima deve ser igual ou maior que a mínima."
    : "";
}

/** Intervalo [de, ate] do período escolhido, ou `null` para "qualquer data". */
export function intervaloDoPeriodo(
  f: FiltrosVisitantes,
  hoje: string = hojeSaoPaulo(),
): [string, string] | null {
  if (f.periodo === "todos") return null;
  if (f.periodo === "hoje") return [hoje, hoje];
  if (f.periodo === "custom") return [f.de || "0000-01-01", f.ate || "9999-12-31"];
  return [somarDias(hoje, -(Number(f.periodo) - 1)), hoje];
}

/** Campos da lista (`visitantes_listar`) que os filtros usam. */
export interface ItemFiltravel {
  nome: string;
  nascimento: string | null;
  telefone_mascarado: string | null;
  visitas: number;
  ultima_visita: string | null;
  datas_visitas: string[];
  contatos: number;
  preferencia_contato: string | null;
}

/**
 * Aplica busca, situação, período (qualquer visita dentro dele), faixa e ordem.
 * Filtros inválidos (datas invertidas / idade mín > máx) não são aplicados — a tela
 * mostra o aviso no lugar.
 */
export function aplicarFiltros<T extends ItemFiltravel>(
  itens: T[],
  f: FiltrosVisitantes,
  hoje: string = hojeSaoPaulo(),
): T[] {
  const termo = normalizarNome(f.busca);
  const termoDigitos = soDigitos(f.busca);
  const intervalo = erroPeriodo(f) ? null : intervaloDoPeriodo(f, hoje);
  const idadeOk = !erroIdade(f);
  const min = f.idadeMin === "" ? null : Number(f.idadeMin);
  const max = f.idadeMax === "" ? null : Number(f.idadeMax);

  const filtrados = itens.filter((v) => {
    if (termo) {
      const porNome = normalizarNome(v.nome).includes(termo);
      // Telefone só aparece mascarado (••••-1234): a busca por número usa os 4 finais.
      const finais = soDigitos(v.telefone_mascarado ?? "");
      const porTelefone =
        termoDigitos.length >= 3 && finais !== "" && finais.includes(termoDigitos);
      if (!porNome && !porTelefone) return false;
    }
    if (f.situacao !== "todos") {
      if (estadoDe(v) !== f.situacao) return false;
      // Criança sem contato não entra em "Não contatado" nem em "Precisa de acompanhamento".
      if (foraDoAcompanhamento(v, hoje)) return false;
    }
    if (intervalo && !v.datas_visitas.some((d) => d >= intervalo[0] && d <= intervalo[1]))
      return false;

    if (f.faixa !== "todas") {
      const idade = calcularIdade(v.nascimento, hoje);
      if (idade === null) return false;
      if (f.faixa === "custom") {
        if (idadeOk && min !== null && idade < min) return false;
        if (idadeOk && max !== null && idade > max) return false;
      } else if (faixaDaIdade(idade).chave !== f.faixa) {
        return false;
      }
    }
    return true;
  });

  const copia = [...filtrados];
  if (f.ordem === "nome") copia.sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  else if (f.ordem === "visitas")
    copia.sort(
      (a, b) =>
        b.visitas - a.visitas || (b.ultima_visita ?? "").localeCompare(a.ultima_visita ?? ""),
    );
  else
    copia.sort(
      (a, b) =>
        (b.ultima_visita ?? "").localeCompare(a.ultima_visita ?? "") ||
        a.nome.localeCompare(b.nome, "pt-BR"),
    );
  return copia;
}
